import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileEdit,
  Kanban
} from 'lucide-react';

interface NewArticleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: string, parentId?: string) => void;
  parentId?: string;
}

export function NewArticleDialog({ isOpen, onClose, onSelect, parentId }: NewArticleDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const fileTypes = [
    { type: 'empty', icon: FileEdit, label: 'Новая статья', description: 'Пустая статья для написания контента' },
    { type: 'kanban', icon: Kanban, label: 'Канбан доска', description: 'Доска для управления задачами' }
  ];

  const handleTypeSelect = (type: string) => {
    onSelect(type, parentId);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
          />
          
          {/* Dialog */}
          <motion.div
            ref={dialogRef}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Выберите тип файла
            </h3>
            
            <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto">
              {fileTypes.map((fileType) => {
                const IconComponent = fileType.icon;
                return (
                  <button
                    key={fileType.type}
                    onClick={() => handleTypeSelect(fileType.type)}
                    className="flex items-center p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors group"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                      <IconComponent className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-grow min-w-0">
                      <h4 className="font-medium text-gray-900 dark:text-white truncate">
                        {fileType.label}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {fileType.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
